import { useNavigate } from "react-router-dom";
import { Zap, Crown, Lock } from "lucide-react";
import { useTier, tierAtLeast } from "@/hooks/useTier";

const TIER_META = {
  pro:   { icon: Zap,   label: "Pro",   color: "text-indigo-400", border: "border-indigo-500/40", bg: "bg-indigo-500/5",  cta: "bg-indigo-500 hover:bg-indigo-400 text-white" },
  elite: { icon: Crown, label: "Elite", color: "text-amber-400",  border: "border-amber-500/40",  bg: "bg-amber-500/5",   cta: "bg-amber-500 hover:bg-amber-400 text-black" },
};

/**
 * Wraps a feature and shows an upgrade wall if the user's tier is below `required`.
 *
 * <TierGate required="pro" feature="AI Copilot" description="Ask anything about markets and strategies.">
 *   <CopilotPanel />
 * </TierGate>
 *
 * Props:
 *   required    — "pro" | "elite"
 *   feature     — display name shown on the gate
 *   description — optional one-liner shown on the gate
 *   compact     — render a small inline badge instead of a full gate panel
 *   children    — rendered when tier is sufficient
 */
export default function TierGate({ required = "pro", feature, description, compact = false, children }) {
  const { tier, loading } = useTier();
  const navigate = useNavigate();

  if (loading) return null;
  if (tierAtLeast(tier, required)) return children;

  const meta = TIER_META[required] || TIER_META.pro;
  const Icon = meta.icon;

  if (compact) {
    return (
      <button
        onClick={() => navigate("/pricing")}
        className={`inline-flex items-center gap-1.5 text-[10px] font-section tracking-wider px-2 py-1 border ${meta.border} ${meta.bg} ${meta.color} hover:opacity-80 transition-opacity`}
      >
        <Lock className="w-2.5 h-2.5" />
        {required.toUpperCase()}
      </button>
    );
  }

  return (
    <div className={`panel p-6 flex flex-col items-center text-center border ${meta.border} ${meta.bg}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 border ${meta.border}`}>
        <Icon className={`w-5 h-5 ${meta.color}`} />
      </div>
      <div className="text-[10px] font-section tracking-widest txt-muted mb-1">
        {meta.label.toUpperCase()} FEATURE
      </div>
      {feature && (
        <div className="text-base font-section text-[var(--text-primary)] mb-2">{feature}</div>
      )}
      {description && (
        <div className="text-sm txt-secondary mb-5 max-w-xs leading-relaxed">{description}</div>
      )}
      <button
        onClick={() => navigate("/pricing")}
        className={`px-6 py-2.5 font-section tracking-wider text-xs transition-colors ${meta.cta}`}
      >
        Upgrade to {meta.label}
      </button>
      <div className="mt-3 text-[10px] txt-muted">
        You&rsquo;re on the <span className="capitalize font-section">{tier}</span> plan
      </div>
    </div>
  );
}

/**
 * Small usage meter bar for free-tier limits.
 * <UsageMeter resource="signals_per_day" label="signals today" />
 */
export function UsageMeter({ resource, label }) {
  const { tier, usage } = useTier();
  const navigate = useNavigate();

  if (!usage || tier === "admin") return null;
  const item = usage[resource];
  if (!item || item.unlimited) return null;

  const pct = Math.min(100, (item.used / item.limit) * 100);
  const exhausted = item.exhausted;
  const barColor = exhausted ? "var(--loss)" : pct >= 66 ? "var(--warn)" : "var(--profit)";

  return (
    <div className="flex items-center gap-2 text-[10px] font-section">
      <span className={exhausted ? "txt-loss" : "txt-muted"}>
        {item.used}/{item.limit} {label}
      </span>
      <div className="w-16 h-1 bg-[var(--bg-surface-2)] relative">
        <div
          className="absolute left-0 top-0 h-full transition-all"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
      {exhausted && (
        <button
          onClick={() => navigate("/pricing")}
          className="txt-accent hover:underline tracking-wider"
        >
          UPGRADE
        </button>
      )}
    </div>
  );
}
